import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { AchievementStats } from './CourseCompletion/achievement-stats';
import { CertificatePreview } from './CourseCompletion/certificate-preview';
import { HeroSection } from './CourseCompletion/hero-section';
import { MasteryTakeaways } from './CourseCompletion/mastery-takeaways';
import { NextSteps } from './CourseCompletion/next-steps';
import { Button } from 'components/ui/button';
import { LoadingAnimation } from 'components/ui/loading-animation';
import { useAuth } from 'contexts/AuthContext';
import { useEventTracking } from 'hooks/useEventTracking';
import { courses, linkedin } from 'services/api';
import type { LinkedInStatus } from 'services/api';
import type { CourseCompletionData } from 'types';

interface CourseCompletionLocationState {
  courseId?: number;
  courseName?: string;
  completedFlowName?: string;
  completedAt?: string;
  fromQuiz?: boolean;
}

type CompletionLoadState = 'none' | 'not_found' | 'forbidden' | 'failed';

const CourseCompletion: React.FC = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { track } = useEventTracking();
  const trackedCourseRef = useRef<number | null>(null);
  const locationState = (location.state ?? null) as CourseCompletionLocationState | null;
  const [completionData, setCompletionData] = useState<CourseCompletionData | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadState, setLoadState] = useState<CompletionLoadState>('none');
  const [linkedinStatus, setLinkedinStatus] = useState<LinkedInStatus | null>(null);
  const [linkedinStatusLoading, setLinkedinStatusLoading] = useState(false);
  const [linkedinPostState, setLinkedinPostState] = useState<'idle' | 'posting' | 'success' | 'error'>('idle');
  const [linkedinPostUrl, setLinkedinPostUrl] = useState<string | null>(null);
  const [linkedinError, setLinkedinError] = useState<string | null>(null);
  const [linkedinErrorCode, setLinkedinErrorCode] = useState<string | null>(null);

  const queryCourseId = useMemo(() => {
    const rawCourseId = searchParams.get('courseId');
    if (!rawCourseId) {
      return null;
    }
    const parsed = Number.parseInt(rawCourseId, 10);
    return Number.isNaN(parsed) ? null : parsed;
  }, [searchParams]);

  const resolvedCourseId = locationState?.courseId ?? queryCourseId;

  useEffect(() => {
    let cancelled = false;

    if (!resolvedCourseId) {
      setCompletionData(null);
      setLoading(false);
      setLoadState('none');
      return () => {
        cancelled = true;
      };
    }

    setLoading(true);
    setCompletionData(null);
    setLoadState('none');

    courses
      .getCourseCompletion(resolvedCourseId)
      .then((response) => {
        if (cancelled) {
          return;
        }
        setCompletionData(response.data);
      })
      .catch((fetchError) => {
        if (cancelled) {
          return;
        }
        console.error('Failed to fetch course completion data', fetchError);
        if (fetchError?.response?.data?.code === 'course_prerequisites_unmet') {
          navigate(`/training?courseId=${resolvedCourseId}#courses`, { replace: true, state: { courseLocked: true } });
          return;
        }
        const statusCode = fetchError?.response?.status;
        if (statusCode === 404) {
          setLoadState('not_found');
          return;
        }
        if (statusCode === 403) {
          setLoadState('forbidden');
          return;
        }
        setLoadState('failed');
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [resolvedCourseId, navigate]);

  useEffect(() => {
    if (!resolvedCourseId) {
      return;
    }

    if (loadState !== 'none') {
      return;
    }

    if (!completionData?.is_completed || completionData.eligibility?.is_locked) {
      return;
    }

    if (trackedCourseRef.current === resolvedCourseId) {
      return;
    }

    trackedCourseRef.current = resolvedCourseId;
    track('course_complete', {
      courseId: resolvedCourseId,
      completedAt: completionData?.completed_at ?? locationState?.completedAt ?? null,
      source: locationState?.fromQuiz ? 'quiz_results' : 'flow_complete',
    });
  }, [completionData?.completed_at, completionData?.is_completed, completionData?.eligibility?.is_locked, loadState, locationState?.completedAt, locationState?.fromQuiz, resolvedCourseId, track]);

  const learnerName = useMemo(() => {
    const fullName = [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim();
    if (fullName) {
      return fullName;
    }
    return user?.username ?? 'Learner';
  }, [user?.first_name, user?.last_name, user?.username]);

  const courseName = completionData?.course.name ?? locationState?.courseName ?? 'Course Completed';
  const courseDescription = completionData?.course.description ?? '';
  const hasCertificate = completionData?.course.has_certificate === true;
  const certificate = completionData?.certificate ?? null;
  const completedAt = completionData?.completed_at ?? locationState?.completedAt ?? null;
  const linkedinReturnPath = useMemo(
    () => buildLinkedInReturnPath(location.pathname, location.search),
    [location.pathname, location.search],
  );
  const learningOutcomes = useMemo(
    () =>
      (completionData?.course.learning_outcomes ?? [])
        .map((outcome) => outcome.trim())
        .filter((outcome) => outcome.length > 0),
    [completionData?.course.learning_outcomes],
  );

  const chapterCount = completionData?.stats.chapter_count ?? 0;
  const completedChapterCount = completionData?.stats.completed_chapter_count ?? 0;
  const flowCount = completionData?.stats.flow_count ?? 0;
  const completedFlowCount = completionData?.stats.completed_flow_count ?? 0;
  const quizCount = completionData?.stats.quiz_count ?? 0;
  const passedQuizCount = completionData?.stats.passed_quiz_count ?? 0;
  const activeVisibleTimeSeconds = completionData?.stats.active_visible_time_seconds ?? 0;
  const backToCoursesPath = resolvedCourseId
    ? `/training?courseId=${resolvedCourseId}#courses`
    : '/training#courses';
  const backToCoursesPathNoCourseId = '/training#courses';

  const refreshLinkedInStatus = useCallback((cancelledRef?: { cancelled: boolean }) => {
    if (!certificate) {
      setLinkedinStatus(null);
      setLinkedinStatusLoading(false);
      return Promise.resolve();
    }

    setLinkedinStatusLoading(true);
    return linkedin
      .getStatus()
      .then((response) => {
        if (!cancelledRef?.cancelled) {
          setLinkedinStatus(response.data);
        }
      })
      .catch((error) => {
        if (!cancelledRef?.cancelled) {
          console.error('Failed to fetch LinkedIn status', error);
          setLinkedinStatus(null);
        }
      })
      .finally(() => {
        if (!cancelledRef?.cancelled) {
          setLinkedinStatusLoading(false);
        }
      });
  }, [certificate]);

  useEffect(() => {
    const cancelledRef = { cancelled: false };

    void refreshLinkedInStatus(cancelledRef);

    return () => {
      cancelledRef.cancelled = true;
    };
  }, [refreshLinkedInStatus]);

  useEffect(() => {
    const publishedLinkedInPost = certificate?.linkedin_post;
    if (publishedLinkedInPost?.status === 'published' && publishedLinkedInPost.linkedin_url) {
      setLinkedinPostState('success');
      setLinkedinPostUrl(publishedLinkedInPost.linkedin_url);
      setLinkedinError(null);
      setLinkedinErrorCode(null);
      return;
    }

    setLinkedinPostState('idle');
    setLinkedinPostUrl(null);
    setLinkedinError(null);
    setLinkedinErrorCode(null);
  }, [
    certificate?.credential_id,
    certificate?.linkedin_post,
  ]);

  useEffect(() => {
    const linkedinErrorCode = searchParams.get('linkedin_error');
    const linkedinConnected = searchParams.get('linkedin_connected');
    if (linkedinErrorCode) {
      setLinkedinError('LinkedIn connection failed. Please try connecting again.');
      setLinkedinErrorCode(linkedinErrorCode);
      setLinkedinPostState('error');
    }
    if (linkedinConnected) {
      setLinkedinError(null);
      setLinkedinErrorCode(null);
      setLinkedinPostState('idle');
      void refreshLinkedInStatus();
    }
    if (linkedinErrorCode || linkedinConnected) {
      navigate(linkedinReturnPath, { replace: true });
    }
  }, [linkedinReturnPath, navigate, refreshLinkedInStatus, searchParams]);

  const handleLinkedInConnect = () => {
    if (!resolvedCourseId || !certificate) {
      return;
    }

    setLinkedinError(null);
    setLinkedinErrorCode(null);
    track('certificate_linkedin_connect_click', {
      courseId: resolvedCourseId,
      credentialId: certificate.credential_id,
    });

    const shouldDisconnectFirst = Boolean(linkedinStatus?.connected);
    const startOAuth = () => linkedin.initiate(linkedinReturnPath);
    const initiateRequest = shouldDisconnectFirst
      ? linkedin
          .disconnect()
          .then(() => {
            setLinkedinStatus((current) =>
              current ? { ...current, connected: false, scopes: [], missing_scopes: [] } : current,
            );
          })
          .catch(() => undefined)
          .then(startOAuth)
      : startOAuth();

    initiateRequest
      .then((response) => {
        window.location.assign(response.data.authorization_url);
      })
      .catch((error) => {
        console.error('Failed to initiate LinkedIn OAuth', error);
        setLinkedinError('Unable to start LinkedIn connection. Please try again.');
        setLinkedinPostState('error');
      });
  };

  const handleLinkedInPost = (commentary: string) => {
    if (!resolvedCourseId || !certificate) {
      return;
    }

    setLinkedinPostState('posting');
    setLinkedinError(null);
    setLinkedinErrorCode(null);
    setLinkedinPostUrl(null);
    track('certificate_linkedin_post_click', {
      courseId: resolvedCourseId,
      credentialId: certificate.credential_id,
    });

    linkedin
      .postCertificate(certificate.credential_id, commentary)
      .then((response) => {
        setLinkedinPostState('success');
        setLinkedinPostUrl(response.data.linkedin_url);
        track('certificate_linkedin_post_success', {
          courseId: resolvedCourseId,
          credentialId: certificate.credential_id,
        });
      })
      .catch((error) => {
        console.error('Failed to post certificate to LinkedIn', error);
        const message = error?.response?.data?.detail || 'Unable to post to LinkedIn. Please try again.';
        const code = error?.response?.data?.code ?? 'unknown';
        setLinkedinError(message);
        setLinkedinErrorCode(code);
        setLinkedinPostState('error');
        track('certificate_linkedin_post_failure', {
          courseId: resolvedCourseId,
          credentialId: certificate.credential_id,
          code,
        });
      });
  };

  const certificatePreview = hasCertificate && certificate ? (
    <CertificatePreview
      learnerName={learnerName}
      courseName={courseName}
      completedAt={completedAt}
      certificate={certificate}
      linkedinStatus={linkedinStatus}
      linkedinStatusLoading={linkedinStatusLoading}
      linkedinPostState={linkedinPostState}
      linkedinPostUrl={linkedinPostUrl}
      linkedinError={linkedinError}
      linkedinErrorCode={linkedinErrorCode}
      onConnectLinkedIn={handleLinkedInConnect}
      onPostLinkedIn={handleLinkedInPost}
    />
  ) : null;

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <LoadingAnimation />
      </div>
    );
  }

  if (!resolvedCourseId && !locationState?.courseName) {
    return (
      <div className="mx-auto flex min-h-[60vh] max-w-xl flex-col items-center justify-center gap-4 px-6 text-center">
        <h1 className="text-2xl font-semibold text-foreground">Course completion</h1>
        <p className="text-sm text-muted-foreground">
          We could not determine which course to show yet.
        </p>
        <Button size="auto" onClick={() => navigate('/home')}>
          Back to Dashboard
        </Button>
      </div>
    );
  }

  if (completionData && (!completionData.is_completed || completionData.eligibility?.is_locked)) {
    return (
      <div className="mx-auto w-full max-w-6xl py-8 sm:py-10">
        <div className={`mx-auto flex max-w-xl flex-col items-center justify-center gap-4 text-center${certificatePreview ? '' : ' min-h-[60vh]'}`}>
          <h1 className="text-2xl font-semibold text-foreground">
            {certificatePreview ? 'Course requirements updated' : 'Course in progress'}
          </h1>
          <p className="text-sm text-muted-foreground">
            {certificatePreview
              ? 'Your earlier completion is saved. You can still view and share it below while working through the current requirements.'
              : 'Finish the current course requirements to unlock your completion summary.'}
          </p>
          <Button size="auto" onClick={() => navigate(backToCoursesPath)}>
            {certificatePreview ? 'View current requirements' : 'Back to My Courses'}
          </Button>
        </div>
        {certificatePreview}
      </div>
    );
  }

  if (loadState === 'not_found') {
    return (
      <div className="mx-auto flex min-h-[60vh] max-w-xl flex-col items-center justify-center gap-4 px-6 text-center">
        <h1 className="text-2xl font-semibold text-foreground">Course not found</h1>
        <p className="text-sm text-muted-foreground">
          We could not find this course. It may have been removed or the link may be invalid.
        </p>
        <Button size="auto" onClick={() => navigate(backToCoursesPathNoCourseId)}>
          Back to My Courses
        </Button>
      </div>
    );
  }

  if (loadState === 'forbidden') {
    return (
      <div className="mx-auto flex min-h-[60vh] max-w-xl flex-col items-center justify-center gap-4 px-6 text-center">
        <h1 className="text-2xl font-semibold text-foreground">No access to this course</h1>
        <p className="text-sm text-muted-foreground">
          This course is not assigned to you, so the completion summary is unavailable.
        </p>
        <Button size="auto" onClick={() => navigate(backToCoursesPathNoCourseId)}>
          Back to My Courses
        </Button>
      </div>
    );
  }

  if (loadState === 'failed') {
    return (
      <div className="mx-auto flex min-h-[60vh] max-w-xl flex-col items-center justify-center gap-4 px-6 text-center">
        <h1 className="text-2xl font-semibold text-foreground">Unable to load course completion</h1>
        <p className="text-sm text-muted-foreground">
          Please try again in a moment.
        </p>
        <Button size="auto" onClick={() => navigate(backToCoursesPath)}>
          Back to My Courses
        </Button>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-6xl py-8 sm:py-10">
      <HeroSection
        courseName={courseName}
        completedAt={completedAt}
        description={courseDescription}
      />
      <AchievementStats
        flowCount={flowCount}
        completedFlowCount={completedFlowCount}
        chapterCount={chapterCount}
        completedChapterCount={completedChapterCount}
        quizCount={quizCount}
        passedQuizCount={passedQuizCount}
        activeVisibleTimeSeconds={activeVisibleTimeSeconds}
      />
      {learningOutcomes.length > 0 ? (
        <MasteryTakeaways takeaways={learningOutcomes} />
      ) : null}
      <NextSteps unlockedCourses={completionData?.unlocked_courses ?? []} />
      {certificatePreview}
    </div>
  );
};

const buildLinkedInReturnPath = (pathname: string, search: string): string => {
  const params = new URLSearchParams(search);
  params.delete('linkedin_connected');
  params.delete('linkedin_error');
  const query = params.toString();
  return query ? `${pathname}?${query}` : pathname;
};

export default CourseCompletion;
